import './price-matrix.css';
import '@kerfjs/ui/select/register';

import { Button } from '@kerfjs/ui/button';
import { uiColor } from '@kerfjs/ui/css-values';
import { List } from '@kerfjs/ui/list';
import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import type { PopupMenuEntry } from '@kerfjs/ui/popup-menu';
import { Row } from '@kerfjs/ui/row';
import { Select } from '@kerfjs/ui/select';
import { Text } from '@kerfjs/ui/text';
import { Fragment, type SafeHtml } from 'kerfjs';
import { Trophy, Unlink } from 'lucide';

import { CurrencyAmount } from '../../../components/currency-amount.js';
import { cellMatch } from '../../../components/ui-helpers.js';
import type { QuoteMatrix } from '../../../data/api.js';
import { PRICE_BASES } from '../../../data/state.js';
import { matrixGrid, type MatrixPosition, matrixPositionKey, matrixTabStop } from '../../../lib/matrix-selection.js';
import { AiDetectedLabel, quoteFromAi } from './ai-detected-label.js';

export interface PriceMatrixProps {
  matrix: QuoteMatrix;
  readOnly: boolean;
  /** Span the container side to side: no side borders or corner radius (HS2-81WWDP). */
  edgeToEdge?: boolean;
  /** Selected cells' quote-line ids (HS2-QV3276). Only an editable matrix is selectable. */
  selectedCells?: readonly string[];
  /** Where the keyboard tab stop rests; it falls back to the first mapped cell. */
  focus?: MatrixPosition | null;
}

/** The Quotes segment's price matrix (HS2-M4258V): demand line items down the side,
 *  one column per vendor quotation (name, AI-detected mark, price-basis Select), each
 *  cell the base-currency price with the row's best (lowest) price marked, and the
 *  unmatched quote lines listed after the matched rows with inline map / new line /
 *  ignore actions. Just the table; the caller supplies the section header. */
export function PriceMatrix({
  matrix,
  readOnly,
  edgeToEdge = false,
  selectedCells = [],
  focus = null,
}: PriceMatrixProps): SafeHtml {
  // An editable matrix is a multi-select grid of its mapped cells (HS2-QV3276): each
  // cell and header carries its position, and one of them is the roving tab stop.
  const selectable = !readOnly;
  const grid = matrixGrid(matrix);
  const tabStop = selectable ? matrixTabStop(grid, focus) : null;
  const selected = new Set(selectedCells);
  const position = (row: number, col: number): Record<string, string> => {
    if (!selectable) return {};
    const isTabStop = tabStop !== null && tabStop.row === row && tabStop.col === col;
    return { 'data-matrix-pos': matrixPositionKey({ row, col }), tabindex: isTabStop ? '0' : '-1' };
  };
  return (
    <div class="price-matrix" data-edge-to-edge={edgeToEdge ? '' : undefined}>
      <table
        class="t matrix"
        role={selectable ? 'grid' : undefined}
        aria-multiselectable={selectable ? 'true' : undefined}
        aria-label={selectable ? 'Price matrix' : undefined}
      >
        <thead>
          <tr>
            <th class="rowhead">
              <Row controlInsets="rl">
                <Text>
                  <strong>Line item</strong>
                </Text>
              </Row>
            </th>
            {matrix.columns.map((col, colIndex) => (
              <th data-key={col.quotationId} class="vendor-col" {...position(-1, colIndex)}>
                <List>
                  <Row gap="2xs" vAlign="middle" controlInsets="rl">
                    <Text>
                      <strong>{col.vendorName}</strong>
                      <AiDetectedLabel detected={quoteFromAi(col.howReceived)} />
                    </Text>
                  </Row>
                  <Row controlInsets="rl">
                    <Select
                      className="basis-select"
                      name={`quote-basis:${col.quotationId}`}
                      value={knownPriceBasis(col.priceBasis)}
                      choices={PRICE_BASES.map((basis) => ({ value: basis, label: priceBasisLabel(basis) }))}
                      disabled={readOnly ? true : undefined}
                      ariaLabel="Price Basis"
                      hint={`Quoted in ${col.currency ?? matrix.base}`}
                    />
                  </Row>
                </List>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.lineItems.map((li, rowIndex) => {
            // Mark the best (lowest) priced cell in the row, only when there's
            // more than one price to compare (HS2-CF15H1, extends HS2-TZM60Z).
            const rowPrices = matrix.columns
              .map((c) => matrix.cells[li.id]?.[c.quotationId]?.basePrice)
              .filter((n): n is number => n != null);
            const rowBest = rowPrices.length > 1 ? Math.min(...rowPrices) : null;
            return (
              <tr data-key={li.id}>
                <th class="rowhead" scope="row" {...position(rowIndex, -1)}>
                  <Row controlInsets="rl">
                    <Text wrap="anywhere">
                      {String(li.lineNumber)}. {li.description}
                      {li.quantity !== null ? (
                        <Text variant="span" tone="quiet" size="compact">
                          {' '}
                          · {li.quantity} {li.unit ?? null}
                        </Text>
                      ) : null}
                    </Text>
                  </Row>
                </th>
                {matrix.columns.map((col, colIndex) => {
                  const cell = matrix.cells[li.id]?.[col.quotationId];
                  if (cell === undefined) {
                    return (
                      <td data-key={col.quotationId}>
                        <Row hAlign="r" controlInsets="rl">
                          <Text tone="quiet">–</Text>
                        </Row>
                      </td>
                    );
                  }

                  const isBest = rowBest !== null && cell.basePrice === rowBest;
                  return (
                    <td
                      data-key={col.quotationId}
                      class="cell-quote"
                      data-match={cellMatch(cell.matchConfidence, cell.matchStatus)}
                      data-best={String(isBest)}
                      title={isBest ? 'Best (lowest) quote for this line' : undefined}
                      data-quote-line-id={selectable ? cell.quoteLineId : undefined}
                      aria-selected={selectable ? String(selected.has(cell.quoteLineId)) : undefined}
                      {...position(rowIndex, colIndex)}
                    >
                      <List hAlign="r">
                        <Row vAlign="middle" controlInsets="rl">
                          {isBest ? (
                            <>
                              <LucideIcon
                                color={uiColor('success-on-quiet')}
                                size="xs"
                                icon={Trophy}
                                name="Best Quote"
                              />
                              <span class="app-visually-hidden">Best quote: </span>
                            </>
                          ) : null}
                          <Text wrap="nowrap">
                            <CurrencyAmount value={cell.basePrice} currency={matrix.base} />
                          </Text>
                        </Row>

                        {cell.currency !== null && cell.currency !== matrix.base && cell.unitPrice !== null ? (
                          <Row controlInsets="rl">
                            <Text tone="quiet" size="compact" wrap="nowrap">
                              <CurrencyAmount value={cell.unitPrice} currency={cell.currency} />
                              {cell.baseRate !== null
                                ? ` · 1 ${cell.currency} = ${String(cell.baseRate)} ${matrix.base}`
                                : null}
                            </Text>
                          </Row>
                        ) : null}
                      </List>
                    </td>
                  );
                })}
              </tr>
            );
          })}
          {matrix.lineItems.length === 0 && matrix.unmatched.length === 0 ? (
            <tr>
              <td colSpan={matrix.columns.length + 1}>
                <Row textInsets="rl">
                  <Text tone="quiet">No line items on this demand yet.</Text>
                </Row>
              </td>
            </tr>
          ) : null}
          {/* Unmatched quote lines list AFTER the matched line items (HS2-YH3P66),
          each flagged with an unmatched status icon and mappable inline. */}
          {matrix.unmatched.map((u) => (
            <Fragment data-key={u.quoteLineId}>
              <tr class="unmatched-row">
                <th class="rowhead" scope="row">
                  <Row vAlign="middle" textInsets="rl">
                    <LucideIcon color={uiColor('warning-on-quiet')} icon={Unlink} name="Unmatched" />
                    <span class="app-visually-hidden">Unmatched: </span>
                    <Text wrap="anywhere">{u.description ?? 'Unlabelled item'}</Text>
                  </Row>
                </th>
                {matrix.columns.map((col) =>
                  col.quotationId === u.quotationId ? (
                    <td data-key={col.quotationId} class="cell-quote">
                      <Row hAlign="r" controlInsets="rl">
                        <Text wrap="nowrap">
                          {u.unitPrice !== null ? (
                            <CurrencyAmount value={u.unitPrice} currency={u.currency ?? matrix.base} />
                          ) : (
                            '—'
                          )}
                        </Text>
                      </Row>
                    </td>
                  ) : (
                    <td data-key={col.quotationId}>
                      <Row hAlign="r" controlInsets="rl">
                        <Text tone="quiet">–</Text>
                      </Row>
                    </td>
                  ),
                )}
              </tr>
              {readOnly ? null : (
                <tr class="unmatched-actions-row">
                  <th class="rowhead" scope="row">
                    <Row controlInsets="rl">
                      <Select
                        name={`quote-map:${u.quoteLineId}`}
                        value=""
                        ariaLabel="Map to Line"
                        presentation="navigation"
                        size="compact"
                        labelMaxWidth={320}
                        choices={[
                          { value: '', label: '— Map to Line —' },
                          ...matrix.lineItems.map((li) => ({
                            value: li.id,
                            label: `${String(li.lineNumber)}. ${li.description}`,
                          })),
                        ]}
                      />
                    </Row>
                  </th>
                  <td colSpan={matrix.columns.length}>
                    <Row gap="xs" vAlign="middle" controlInsets="rl">
                      <Button
                        tone="neutral"
                        emphasis="primary"
                        size="compact"
                        rootAttributes={{ 'data-quote-addline': u.quoteLineId }}
                      >
                        + New Line
                      </Button>
                      <Button
                        tone="neutral"
                        size="compact"
                        emphasis="plain"
                        rootAttributes={{ 'data-quote-ignore': u.quoteLineId }}
                      >
                        Ignore
                      </Button>
                    </Row>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function priceBasisLabel(basis: (typeof PRICE_BASES)[number]): string {
  if (basis === 'ex-works') return 'Ex works';
  if (basis === 'delivered-to-site') return 'Delivered to site';
  if (basis === 'fob') return 'FOB — free on board';
  if (basis === 'cif') return 'CIF — cost, insurance, freight';
  return 'Unknown — needs review';
}

function knownPriceBasis(basis: string | null): (typeof PRICE_BASES)[number] {
  return PRICE_BASES.find((candidate) => candidate === basis) ?? 'unknown';
}

/** The price matrix's right-click menu (HS2-QV3276): unmap the selected cells, which
 *  sends each quote line back to the unmatched list. */
export function matrixCellMenuEntries(count: number): PopupMenuEntry[] {
  return [
    {
      label: count === 1 ? 'Unmap' : `Unmap ${String(count)} Cells`,
      icon: <LucideIcon icon={Unlink} name="" />,
      action: 'matrix-unmap',
    },
  ];
}
